import { Box, Flex } from "@chakra-ui/react";
import { ChainId, useEthers } from "@usedapp/core";
import { signInAnonymously } from "firebase/auth";
import { get } from "lodash-es";
import React, { Suspense, useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { Navigate, Outlet, Route, Routes, useLocation } from "react-router";
import { AppFooter } from "../components/AppFooter";
import AppHeader from "../components/AppHeader";
import { DelayedRender } from "../components/DeplayedRenderer";
import { WrongNetworkModal } from "../components/WrongNetworkModal";
import { env } from "../core/environment";
import { auth } from "../core/firebase.provider";
import AssetDetail from "../pages/AssestDetail";
import Assets from "../pages/Assets";
import ChooseAssetType from "../pages/ChooseAssetType";
import CollectionDetail from "../pages/CollectionDetail";
import ConnectWallet from "../pages/ConnectWallet";
import CreateAsset from "../pages/CreateAsset";
import Dashboard from "../pages/Dashboard";
import Marketplace from "../pages/Marketplace";
import ProfileCollections from "../pages/ProfileCollections";
import ProfileSetting from "../pages/ProfileSetting";
import Purchases from "../pages/Purchases";
import SaleHistory from "../pages/SaleHistory";
import { userService } from "../services/user.service";
import { setSupportedTokens } from "../store/features/token/token.slice";
import { logout, setUser } from "../store/features/user/user.slice";
import { useAppDispatch, useAppSelector } from "../store/hook";
import { ProfileModule } from "./ProfileModule";

function Router() {
  const dispatch = useAppDispatch();
  const { account, chainId } = useEthers();
  const user = useAppSelector((state) => state.user);
  const [isWrongNetwork, setIsWrongNetwork] = useState(false);

  useEffect(() => {
    const network = get(env.network, [env.config.supported_chain_ids[0]], env.network[ChainId.BSC]);
    const bERC20Instances = network?.btokens?.berc20?.instances;
    const bEther = network?.btokens?.bether;
    dispatch(
      setSupportedTokens([
        {
          address: bERC20Instances.usdc.address,
          logoUrl:
            "https://firebasestorage.googleapis.com/v0/b/bepay-live.appspot.com/o/tokens%2Fusdc.svg?alt=media&token=094e687e-05c5-4b61-948f-0a1308496e21",
          symbol: "USDC",
          underlying: bERC20Instances.usdc.underlying,
          decimals: bERC20Instances.usdc.decimals,
        },
        {
          address: bERC20Instances.usdt.address,
          logoUrl:
            "https://firebasestorage.googleapis.com/v0/b/bepay-live.appspot.com/o/tokens%2Fusdt.svg?alt=media&token=0046e876-759f-4c44-995f-875ed8d92dd5",
          symbol: "USDT",
          underlying: bERC20Instances.usdt.underlying,
          decimals: bERC20Instances.usdt.decimals,
        },
        {
          address: bERC20Instances.busd.address,
          logoUrl:
            "https://firebasestorage.googleapis.com/v0/b/bepay-live.appspot.com/o/tokens%2Fbusd.png?alt=media&token=8d3c172f-cfdd-4162-9a3f-e8f5cf59a17c",
          symbol: "BUSD",
          underlying: bERC20Instances.busd.underlying,
          decimals: bERC20Instances.busd.decimals,
        },
        {
          address: bEther.address,
          logoUrl:
            "https://firebasestorage.googleapis.com/v0/b/bepay-live.appspot.com/o/tokens%2Fbnb.png?alt=media&token=9d97f264-4e79-47d8-9973-6145f1e0a736",
          symbol: "BNB",
          underlying: "bnb",
          decimals: bEther.decimals,
        },
      ])
    );
  }, [chainId]);

  useEffect(() => {
    if (account && chainId && !env.config.supported_chain_ids.includes(chainId)) {
      setIsWrongNetwork(true);
    } else {
      setIsWrongNetwork(false);
    }
  }, [chainId, account]);

  useEffect(() => {
    async function addAccountToSync() {
      if (!account) return;
      await userService.watch(account);
    }

    addAccountToSync();
  }, [account]);

  useEffect(() => {
    async function getProfile() {
      if (!account) return;
      const response = await userService.getProfileById(account);

      if (response.data) {
        dispatch(setUser(response.data));
      }
    }

    getProfile();
  }, [account]);

  useEffect(() => {
    if (account && user?.profile?.id && account?.toLowerCase() !== user?.profile?.id) {
      dispatch(logout());
    }
  }, [account]);

  useEffect(() => {
    signInAnonymously(auth);
  }, []);

  return (
    <Flex minHeight={"100vh"} flexDirection="column">
      <Helmet>
        <meta charSet="utf-8" />
        <meta name="description" content={"Turn your products or services into publicly tradable items"} />
        <meta property="og:description" content={"Turn your products or services into publicly tradable items"} />
        <meta property="og:title" content={"bePAY: NFT Marketplace"} />
        <meta
          property="og:image"
          content={
            "https://firebasestorage.googleapis.com/v0/b/bepay-dev.appspot.com/o/logo.png?alt=media&token=145bfe9e-aaca-485f-8d8c-e1c65389420b"
          }
        ></meta>
        <meta property="og:type" content="article" />
        <title>bePAY NFT Marketplace</title>
      </Helmet>
      <AppHeader />
      <Box position={"relative"} flex={1}>
        <Routes>
          <Route index element={<Dashboard />} />
          <Route path="marketplace" element={<Marketplace />} />
          <Route path="profile">
            <Route element={<ProfileModule />}>
              <Route path="assets/owned" element={<RequiredWalletAccount element={<Assets />} />} />
              <Route path="assets/:id" element={<Assets />} />
              <Route path="sale-history" element={<RequiredWalletAccount element={<SaleHistory />} />} />
              <Route path="purchases" element={<RequiredWalletAccount element={<Purchases />} />} />
              <Route path="collections/owned" element={<RequiredWalletAccount element={<ProfileCollections />} />} />
              <Route path="collections/:id" element={<ProfileCollections />} />
            </Route>

            <Route path="setting" element={<RequiredWalletAccount element={<ProfileSetting />} />} />
          </Route>

          <Route path="create" element={<RequiredWalletAccount element={<Outlet />} />}>
            <Route path="start" element={<ChooseAssetType />} />
            <Route path="erc-721" element={<CreateAsset type="ERC721" />} />
            <Route path="erc-1155" element={<CreateAsset type="ERC1155" />} />
          </Route>

          <Route path="collections">
            <Route path=":id" element={<CollectionDetail />} />
          </Route>

          <Route path="connect" element={<ConnectWallet />} />
          <Route path="assets/:tokenAddress/:tokenId" element={<AssetDetail />} />
        </Routes>
      </Box>
      <AppFooter />

      <WrongNetworkModal isVisible={isWrongNetwork} />
    </Flex>
  );
}

const RequiredWalletAccount = (props: { element: React.ReactElement | null }) => {
  const location = useLocation();
  const { account } = useEthers();

  return (
    <Suspense fallback={<Box></Box>}>
      <DelayedRender delay={200}>
        {!!account ? props.element : <Navigate replace to={"/connect"} state={{ from: location }} />}
      </DelayedRender>
    </Suspense>
  );
};

export default React.memo(Router);
